import { Controller } from 'stimulus'
import { html } from 'htm/preact'
import { render } from 'preact'
import { stringify } from 'utils/qs'
import { debouncePromise } from 'utils/index'

const SEARCH_ICON_PATH =
  'M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z'

function createSearchIcon () {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('width', '20')
  svg.setAttribute('height', '20')
  svg.setAttribute('fill', 'currentColor')
  svg.setAttribute('class', 'aa-SubmitIcon')

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', SEARCH_ICON_PATH)
  svg.appendChild(path)
  return svg
}

function buildSourceSection ({ source, items, state, components, activeItemIndex }) {
  const headerTemplate = source.templates?.header
  const itemTemplate = source.templates?.item
  const footerTemplate = source.templates?.footer
  const noResultsTemplate = source.templates?.noResults

  const shouldRenderNoResults = Boolean(noResultsTemplate) && items.length === 0

  const header = headerTemplate
    ? html`<div class="aa-SourceHeader">${headerTemplate({
        components,
        items,
        source,
        state,
        html
      })}</div>`
    : null

  const footer = footerTemplate
    ? html`<div class="aa-SourceFooter">${footerTemplate({
        components,
        items,
        source,
        state,
        html
      })}</div>`
    : null

  const body =
    shouldRenderNoResults
      ? html`<div class="aa-SourceNoResults">${noResultsTemplate({
          components,
          items,
          source,
          state,
          html
        })}</div>`
      : items.length
        ? html`
            <ul class="aa-List">
              ${items.map((item, index) =>
                html`<li
                  class="aa-Item"
                  aria-selected="${index === activeItemIndex}"
                >
                  ${itemTemplate
                    ? itemTemplate({
                        item,
                        components,
                        state,
                        source,
                        html
                      })
                    : null}
                </li>`
              )}
            </ul>
          `
        : null

  return html`<section class="aa-Source" data-autocomplete-source-id="${source.sourceId}">${header}${body}${footer}</section>`
}

function autocomplete (options) {
  const {
    container,
    placeholder = 'Search',
    plugins = [],
    render: renderFn,
    openOnFocus = false,
    initialState = {},
    detachedMediaQuery = '(max-width: 768px)',
    translations = {},
    onSubmit
  } = options

  if (!container) {
    throw new Error('[autocomplete] Missing container')
  }

  const makeEl = (tag, className) => {
    const node = document.createElement(tag)
    if (className) node.className = className
    return node
  }

  const root = makeEl('div', 'aa-Autocomplete')

  const form = makeEl('form', 'aa-Form')
  const inputWrapperPrefix = makeEl('div', 'aa-InputWrapperPrefix')
  const submitButton = makeEl('button', 'aa-SubmitButton')
  submitButton.type = 'submit'
  submitButton.title = translations.submitButtonTitle || 'Submit'
  inputWrapperPrefix.appendChild(submitButton)

  const inputWrapper = makeEl('div', 'aa-InputWrapper')
  const input = makeEl('input', 'aa-Input')
  input.type = 'search'
  input.placeholder = placeholder
  inputWrapper.appendChild(input)

  const inputWrapperSuffix = makeEl('div', 'aa-InputWrapperSuffix')
  const clearButton = makeEl('button', 'aa-ClearButton')
  clearButton.type = 'reset'
  clearButton.title = translations.clearButtonTitle || 'Clear'
  inputWrapperSuffix.appendChild(clearButton)

  // Keep the markup compatible with our existing CSS overrides.
  const panel = makeEl('div', 'aa-Panel')
  panel.style.display = 'none'

  const detachedOverlay = makeEl('div', 'aa-DetachedOverlay')
  const detachedContainer = makeEl('div', 'aa-DetachedContainer')
  const detachedFormContainer = makeEl('div', 'aa-DetachedFormContainer')
  const cancelButton = makeEl('button', 'aa-DetachedCancelButton')
  cancelButton.type = 'button'
  cancelButton.textContent = translations.detachedCancelButtonText || 'Cancel'

  detachedFormContainer.appendChild(form)
  detachedFormContainer.appendChild(cancelButton)
  detachedContainer.appendChild(detachedFormContainer)
  detachedOverlay.appendChild(detachedContainer)

  const detachedSearchButtonIcon = makeEl('div', 'aa-DetachedSearchButtonIcon')
  detachedSearchButtonIcon.appendChild(createSearchIcon())

  const detachedSearchButtonPlaceholder = makeEl(
    'div',
    'aa-DetachedSearchButtonPlaceholder'
  )
  detachedSearchButtonPlaceholder.textContent = placeholder

  const detachedSearchButtonQuery = makeEl(
    'div',
    'aa-DetachedSearchButtonQuery'
  )

  const detachedSearchButton = makeEl(
    'button',
    'aa-DetachedSearchButton'
  )
  detachedSearchButton.type = 'button'
  detachedSearchButton.appendChild(detachedSearchButtonIcon)
  detachedSearchButton.appendChild(detachedSearchButtonPlaceholder)
  detachedSearchButton.appendChild(detachedSearchButtonQuery)

  submitButton.appendChild(createSearchIcon())
  form.appendChild(inputWrapperPrefix)
  form.appendChild(inputWrapper)
  form.appendChild(inputWrapperSuffix)

  const setPanelPosition = () => {
    const containerRect = container.getBoundingClientRect()
    const formRect = form.getBoundingClientRect()

    const top = window.scrollY + containerRect.top + containerRect.height
    const left = formRect.left
    const right = window.innerWidth - (formRect.left + formRect.width)

    panel.style.top = `${top}px`
    panel.style.left = `${left}px`
    panel.style.right = `${right}px`
  }

  const detachedMedia = window.matchMedia(detachedMediaQuery)
  const modalMediaQuery = window.matchMedia(
    window.getComputedStyle(document.documentElement).getPropertyValue('--aa-detached-modal-media-query') ||
      '(min-width: 680px)'
  )

  function isDetachedNow () {
    return detachedMedia.matches
  }

  function updateDetachedModalClass () {
    detachedContainer.classList.toggle(
      'aa-DetachedContainer--modal',
      modalMediaQuery.matches
    )
  }

  function updateDetachedSearchButtonState (query) {
    detachedSearchButtonQuery.textContent = query || ''
    detachedSearchButtonPlaceholder.hidden = Boolean(query)
  }

  function renderSearchButtonMode () {
    if (isDetachedNow()) {
      if (root.contains(form)) root.removeChild(form)
      if (!detachedFormContainer.contains(form)) {
        detachedFormContainer.insertBefore(form, cancelButton)
      }
      if (!root.contains(detachedSearchButton)) root.appendChild(detachedSearchButton)
    } else {
      if (detachedFormContainer.contains(form)) detachedFormContainer.removeChild(form)
      if (!root.contains(form)) root.appendChild(form)
      if (root.contains(detachedSearchButton)) root.removeChild(detachedSearchButton)
    }
  }

  let isOpen = false
  let refreshRequestId = 0

  function setIsModalOpen (value) {
    if (value) {
      document.body.appendChild(detachedOverlay)
      document.body.classList.add('aa-Detached')
    } else {
      if (detachedOverlay.parentElement) {
        detachedOverlay.parentElement.removeChild(detachedOverlay)
      }
      document.body.classList.remove('aa-Detached')
    }
  }

  function setIsOpen (value) {
    if (value === isOpen) return
    isOpen = value

    if (isDetachedNow()) {
      setIsModalOpen(isOpen)

      if (isOpen) {
        if (!detachedContainer.contains(panel)) detachedContainer.appendChild(panel)
        panel.style.display = ''
      } else {
        panel.style.display = 'none'
        if (detachedContainer.contains(panel)) detachedContainer.removeChild(panel)
      }
    } else {
      setIsModalOpen(false)
      panel.style.display = isOpen ? '' : 'none'

      if (isOpen) {
        if (!document.body.contains(panel)) document.body.appendChild(panel)
        setPanelPosition()
      } else if (document.body.contains(panel)) {
        document.body.removeChild(panel)
      }
    }

    if (isOpen) refresh(input.value)
    else panel.innerHTML = ''
  }

  async function refresh (query) {
    const requestId = ++refreshRequestId
    const state = { query }
    const elements = {}

    try {
      for (const plugin of plugins) {
        if (!plugin?.getSources) continue

        const sources = await plugin.getSources({ query })

        for (const source of sources || []) {
          const getItems = source.getItems
          const items = getItems ? await getItems() : []

          if (!Array.isArray(items)) continue

          const hasNoResultsTemplate = Boolean(source.templates?.noResults)
          if (!hasNoResultsTemplate && items.length === 0) continue

          elements[source.sourceId] = buildSourceSection({
            source,
            items,
            state,
            components: {},
            activeItemIndex: 0
          })
        }
      }

      if (requestId !== refreshRequestId) return

      if (renderFn) {
        renderFn({ elements, state }, panel)
      }
    } catch (e) {
      // Keep UI resilient.
    }
  }

  function onSubmitForm (event) {
    event.preventDefault()
    if (!onSubmit) return
    onSubmit({ state: { query: input.value } }, event)
  }

  function onKeyDown (event) {
    if (event.key === 'Escape') setIsOpen(false)
  }

  function onInput () {
    updateDetachedSearchButtonState(input.value)
    if (isOpen) refresh(input.value)
  }

  function onFocus () {
    if (!openOnFocus) return
    setIsOpen(true)
  }

  // Mount root
  container.innerHTML = ''
  root.appendChild(panel)
  container.appendChild(root)

  // Initial state
  const initialQuery = initialState.query || ''
  input.value = initialQuery
  updateDetachedSearchButtonState(initialQuery)

  // Attach search button or form depending on viewport.
  renderSearchButtonMode()
  updateDetachedModalClass()

  detachedMedia.addEventListener('change', () => {
    renderSearchButtonMode()

    if (isOpen) {
      setIsOpen(false)
      setIsOpen(true)
    }
  })

  modalMediaQuery.addEventListener('change', updateDetachedModalClass)

  input.addEventListener('input', onInput)
  input.addEventListener('focus', onFocus)
  input.addEventListener('keydown', onKeyDown)
  form.addEventListener('submit', onSubmitForm)

  cancelButton.addEventListener('click', () => setIsOpen(false))
  detachedSearchButton.addEventListener('click', () => setIsOpen(true))
  detachedOverlay.addEventListener('mousedown', e => {
    if (e.target === detachedOverlay) setIsOpen(false)
  })

  function outsidePointerDown (event) {
    if (!isOpen) return
    if (isDetachedNow()) return

    const target = event.target
    const clickedInsideRoot = root.contains(target)
    const clickedInsidePanel = panel.contains(target)

    if (!clickedInsideRoot && !clickedInsidePanel) setIsOpen(false)
  }

  document.addEventListener('pointerdown', outsidePointerDown)

  return {
    setIsOpen,
    refresh,
    setActiveItemId () {},
    setQuery (q) {
      input.value = q
      updateDetachedSearchButtonState(q)
      if (isOpen) refresh(q)
    },
    destroy () {
      document.removeEventListener('pointerdown', outsidePointerDown)
      detachedMedia.removeEventListener('change', renderSearchButtonMode)
      modalMediaQuery.removeEventListener('change', updateDetachedModalClass)

      setIsOpen(false)

      if (root.parentElement) root.parentElement.removeChild(root)
      if (detachedOverlay.parentElement) detachedOverlay.parentElement.removeChild(detachedOverlay)
      panel.innerHTML = ''
    }
  }
}

const DEBOUNCE_MS = 500
const COUNTRY = window.env.COUNTRY
const LANGUAGE = window.env.LANGUAGE
const AUTOCOMPLETE_PATH = window.env.AUTOCOMPLETE_PATH
const SEARCH_PATH = window.env.SEARCH_PATH
const SHOP_PATH = window.env.SHOP_PATH
const ITEMS_PATH_SEGMENT = window.env.ITEMS_PATH_SEGMENT
const COUNTRY_CODES = ['de', 'at', 'it']
const LANGUAGE_CODES = ['de', 'en', 'it']

const DEFAULT_CATEGORIES = [{ categoryName: 'sofas' }, { categoryName: 'rugs' }, { categoryName: 'pendant_lighting' }]
const DEFAULT_GERMAN_PRODUCTS = [
  {
    id_i: 172617,
    article_number: '2416',
    ean: '5710302024165',
    brand_name: 'Umage',
    title: 'Cassini Pendelleuchte Mini Weiß',
    category: 'lighting',
    category_name: 'Lampen',
    subcategory: 'pendant_lighting',
    subcategory_name: 'Pendelleuchten',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom',
      'office',
      'bathroom',
      'kitchen_diningroom',
      'hallway'
    ],
    country_of_production: null,
    primary_material: 'Stahl',
    secondary_material: 'Aluminium',
    main_color: 'Weiß',
    style: 'Minimalistisch',
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/cassini-pendelleuchte-mini-weiss',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBOE9RaGc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--0c1a325b89aea608f49538d282f0d42a925263db/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lKYW5CbFp3WTZCa1ZVT2hSeVpYTnBlbVZmZEc5ZmJHbHRhWFJiQjJrQzRBRnBBdUFCIiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--967306f8e3101f8fd891bd62ff1ff28c293c6243/UMAGE_lifestyle_Cassini_mini_white.jpeg',
    height: 11,
    width: 20,
    depth: 20,
    retail_price: 113.0,
    retail_price_i: 113,
    sale_price: 113.0,
    original_price: 226.0,
    discount_percentage: 50,
    has_discount: true,
    premium: false,
    sorting: 1762341387,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '2416',
    height_width: null,
    objectID: '172617',
    _highlightResult: {
      id_i: {
        value: '172617',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '5710302024165',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Umage',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Cassini Pendelleuchte Mini Weiß',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'lighting',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Lampen',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'pendant_lighting',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Pendelleuchten',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'office',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bathroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'kitchen_diningroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'hallway',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Stahl',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Aluminium',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Weiß',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Minimalistisch',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '2416',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 157096,
    article_number: '1000416861',
    ean: '5704924014512',
    brand_name: 'design for the people',
    title: '1-Flammig Wandleuchte Shapes Aluminium Messing',
    category: 'lighting',
    category_name: 'Lampen',
    subcategory: 'wall_lamps',
    subcategory_name: 'Wandleuchten',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom',
      'office',
      'hallway'
    ],
    country_of_production: null,
    primary_material: 'Glas',
    secondary_material: 'Aluminium',
    main_color: 'Gold',
    style: 'Zeitgenössisch',
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in sehr gutem Zustand. Das Produkt weist kleine Schönheitsfehler auf, in der Regel sind das oberflächliche Verschmutzungen, unauffällige Kratzer oder Makel im nicht sichtbaren Bereich.',
    status_of_condition: 'Sehr guter Zustand',
    pdp_url: 'https://www.cocoli.com/de/produkte/1-flammig-wandleuchte-shapes-aluminium-messing',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBelUwYnc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--91999bfaa79314bace252d369d90d36f803ea83c/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/-1000416861-240119-021-MOOD-DETAILS-P000000001000416861-mood.jpg',
    height: 26,
    width: 22,
    depth: 23,
    retail_price: 94.0,
    retail_price_i: 94,
    sale_price: 75.0,
    original_price: 125.0,
    discount_percentage: 40,
    has_discount: true,
    premium: false,
    sorting: 1762323986,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1
    ],
    ext_sku: '1000416861',
    height_width: null,
    objectID: '157096',
    _highlightResult: {
      id_i: {
        value: '157096',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '5704924014512',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'design for the people',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '1-Flammig Wandleuchte Shapes Aluminium Messing',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'lighting',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Lampen',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'wall_lamps',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Wandleuchten',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'office',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'hallway',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Glas',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Aluminium',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Gold',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Zeitgenössisch',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Sehr guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '1000416861',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 136253,
    article_number: '83118650',
    ean: '5714714135662',
    brand_name: 'TEMPUR',
    title: 'PRO® CoolQuilt Matratze 23cm Mittelfest 90cm Breite',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'beds',
    subcategory_name: 'Betten',
    subsubcategory: [
      'mattresses'
    ],
    subsubcategory_name: 'Matratzen',
    room_tags: [
      'bedroom'
    ],
    country_of_production: 'Dänemark',
    primary_material: 'Textil',
    secondary_material: null,
    main_color: 'Weiß',
    style: null,
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Diese Tempur® Matratzen stammen aus retournierten Produkten, die entweder direkt von Tempur® oder über autorisierte Händler verkauft wurden. Diese Rücksendungen erfolgen häufig nach einer Probeschlafphase, wenn sich der Kunde für ein anderes Modell entschieden hat. Die Matratzen werden sorgfältig geprüft und gereinigt. Die Bezüge werden bei maximal 60 °C desinfiziert und bei Bedarf durch Original Tempur® Bezüge ersetzt. Kleine Schönheitsfehler wie leichte Verfärbungen oder Abnutzungserscheinungen am Matratzenkern oder minimale Beschädigungen oder Abnutzungserscheinungen an der Außenhülle können auftreten, aber jede Matratze entspricht den hohen Tempur® Qualitätsstandards und ist hygienisch einwandfrei.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/pro-coolquilt-matratze-23cm-medium-firm-90cm-breite',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBeTV0WWc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--69e336641c797df7a280ca23279faf9840b8091a/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/1_Tempur+Pro+CoolQuilt+Single+in+Dark+Grey+Medium+Firm+Hybrid+1_FFFFFF.jpg',
    height: 200,
    width: 90,
    depth: 23,
    retail_price: 879.0,
    retail_price_i: 879,
    sale_price: 879.0,
    original_price: 2198.0,
    discount_percentage: 60,
    has_discount: true,
    premium: false,
    sorting: 1762323948,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2
    ],
    ext_sku: '83118650',
    height_width: 'ca. 200 x 90',
    objectID: '136253',
    _highlightResult: {
      id_i: {
        value: '136253',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '5714714135662',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'TEMPUR',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'PRO® CoolQuilt Matratze 23cm Mittelfest 90cm Breite',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'beds',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Betten',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'mattresses',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Matratzen',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Dänemark',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Textil',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Weiß',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '83118650',
        matchLevel: 'none',
        matchedWords: []
      },
      height_width: {
        value: 'ca. 200 x 90',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 151114,
    article_number: 'xJUAkJORp',
    ean: null,
    brand_name: 'MYCS',
    title: 'Pyllow Ecksofa Webstoff Anthrazit',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches'
    ],
    subsubcategory_name: 'Sofas & Couches',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Baumwolle',
    secondary_material: 'Leinen',
    main_color: 'Anthrazit',
    style: 'Modern',
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in sehr gutem Zustand. Das Produkt weist kleine Schönheitsfehler auf, in der Regel sind das oberflächliche Verschmutzungen, unauffällige Kratzer oder Makel im nicht sichtbaren Bereich.',
    status_of_condition: 'Sehr guter Zustand',
    pdp_url: 'https://www.cocoli.com/de/produkte/pyllow-ecksofa-webstoff-anthrazit',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBd3l1ZWc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--d1e8790a30b0074fb34953f4558e383bbf5c2268/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBPZ2h3Ym1jNkZISmxjMmw2WlY5MGIxOXNhVzFwZEZzSGFRTGdBV2tDNEFFPSIsImV4cCI6bnVsbCwicHVyIjoidmFyaWF0aW9uIn19--70f730e19e2d86b4a3aff00d1944d6555a3a1596/27d7529ba18254eae2615c7aa10e5491.webp',
    height: 72,
    width: 310,
    depth: 168,
    retail_price: 989.0,
    retail_price_i: 989,
    sale_price: 989.0,
    original_price: 2391.0,
    discount_percentage: 59,
    has_discount: true,
    premium: false,
    sorting: 1762257113,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: 'xJUAkJORp',
    height_width: null,
    objectID: '151114',
    _highlightResult: {
      id_i: {
        value: '151114',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'MYCS',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Pyllow Ecksofa Webstoff Anthrazit',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'sofas',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Sofas & Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'sofas_and_couches',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Sofas & Couches',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Baumwolle',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Leinen',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Anthrazit',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Modern',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Sehr guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: 'xJUAkJORp',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 155204,
    article_number: '12123154',
    ean: null,
    brand_name: 'Beatris',
    title: 'Versal 180 Boxspringbett Silber',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'beds',
    subcategory_name: 'Betten',
    subsubcategory: [
      'double_beds'
    ],
    subsubcategory_name: 'Doppelbetten',
    room_tags: [
      'bedroom'
    ],
    country_of_production: 'Polen',
    primary_material: 'Holz',
    secondary_material: 'Polyester',
    main_color: 'Silber',
    style: null,
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in gutem Zustand. Das Produkt weist geringe Mängel auf, In der Regel sind das leichte bis mittlere Verschmutzungen, Kratzer oder Makel, auch im sichtbaren Bereich. Das Produkt ist voll funktionsfähig. Die Bodenplane ist an manchen Stellen eingerissen, das Kopfende weist Bohrungen auf und der Stoff ist stellenweise verschmutzt. Bitte beachte die Zustandsbilder.',
    status_of_condition: 'Guter Zustand',
    pdp_url: 'https://www.cocoli.com/de/produkte/versal-180-boxspringbett-silber',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMnpCWlE9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--a524c74063a95a60aeba99b4b5a2632c7da24cae/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/PS_12123154_4.jpg',
    height: 133,
    width: 196,
    depth: 212,
    retail_price: 600.0,
    retail_price_i: 600,
    sale_price: 600.0,
    original_price: 2999.0,
    discount_percentage: 80,
    has_discount: true,
    premium: false,
    sorting: 1762249586,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '12123154',
    height_width: null,
    objectID: '155204',
    _highlightResult: {
      id_i: {
        value: '155204',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Beatris',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Versal 180 Boxspringbett Silber',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'beds',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Betten',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'double_beds',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Doppelbetten',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Polen',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Holz',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Silber',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '12123154',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 151536,
    article_number: '125605',
    ean: null,
    brand_name: 'Verpan',
    title: 'Easy Chair Dedar Karandash 006 Blau',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'armchairs'
    ],
    subsubcategory_name: 'Sessel',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Textil',
    secondary_material: null,
    main_color: 'Blau',
    style: 'Skandinavisch',
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in gutem Zustand. Das Produkt weist geringe Mängel auf, In der Regel sind das leichte bis mittlere Verschmutzungen, Kratzer oder Makel, auch im sichtbaren Bereich. Das Produkt ist voll funktionsfähig. Der Sessel ist an der Seite leicht beschädigt und der Fuß weist Kratzer auf. Bitte beachte die Zustandsbilder.',
    status_of_condition: 'Guter Zustand',
    pdp_url: 'https://www.cocoli.com/de/produkte/easy-chair-dedar-karandash-006-blau',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBNDhkV3c9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--3a2c3ade7b8b60e051cef12d314db948d74d0af1/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lKYW1acFpnWTZCa1ZVT2hSeVpYTnBlbVZmZEc5ZmJHbHRhWFJiQjJrQzRBRnBBdUFCIiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--643aaa5dbe434096b66bdf621b220d2c3aecbd19/proxy-image%20(3).jfif',
    height: 76,
    width: 80,
    depth: 80,
    retail_price: 1535.0,
    retail_price_i: 1535,
    sale_price: 1535.0,
    original_price: 3069.0,
    discount_percentage: 50,
    has_discount: true,
    premium: true,
    sorting: 1762248866,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '125605',
    height_width: null,
    objectID: '151536',
    _highlightResult: {
      id_i: {
        value: '151536',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Verpan',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Easy Chair Dedar Karandash 006 Blau',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'sofas',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Sofas & Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'armchairs',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Textil',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Blau',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Skandinavisch',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '125605',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 100543,
    article_number: 'TG-373',
    ean: null,
    brand_name: 'Design by Michel Ducaroy',
    title: 'Togo Sofa 2-Sitzer Samt Olivgrün',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'two_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,2-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Samt',
    secondary_material: null,
    main_color: 'Grün',
    style: 'Modern',
    period: '1970er',
    type_of_condition: 'Neu aufgearbeitet',
    describe_condition: 'Es handelt sich um ein mit neuen Stoffen aufgearbeitetes Vintage Produkt. Besondere Makel sind nicht bekannt, aber wie bei jedem Vintage Produkt können Gebrauchsspuren vorhanden sein, die durch den Lauf der Zeit entstanden sind. Die originalen Markenetiketten werden nach der Aufbereitung nicht wieder aufgenäht. Alle Togo Sofas werden mit einem gedruckten Authentizitätszertifikat vom Polsterer geliefert.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/togo-sofa-2-sitzer-samt-gruen',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBNFUwVGc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--dd2525f32f40a8cce2c415bb397428315d9e6dec/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBPZ2h3Ym1jNkZISmxjMmw2WlY5MGIxOXNhVzFwZEZzSGFRTGdBV2tDNEFFPSIsImV4cCI6bnVsbCwicHVyIjoidmFyaWF0aW9uIn19--70f730e19e2d86b4a3aff00d1944d6555a3a1596/373a.webp',
    height: 70,
    width: 130,
    depth: 100,
    retail_price: 2495.0,
    retail_price_i: 2495,
    sale_price: 2495.0,
    original_price: null,
    discount_percentage: 0,
    has_discount: false,
    premium: true,
    sorting: 1762182000,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2
    ],
    ext_sku: 'TG-373',
    height_width: null,
    objectID: '100543',
    _highlightResult: {
      id_i: {
        value: '100543',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Design by Michel Ducaroy',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Togo Sofa 2-Sitzer Samt Olivgrün',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'sofas',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Sofas & Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'sofas_and_couches',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'two_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Sofas & Couches,2-Sitzer',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Samt',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Grün',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Modern',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '1970er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Neu aufgearbeitet',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: 'TG-373',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 172291,
    article_number: '252812',
    ean: null,
    brand_name: 'SOFACOMPANY',
    title: 'Umi Sitz- und Essbank Free Sage',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'chairs',
    subcategory_name: 'Stühle',
    subsubcategory: [
      'benches'
    ],
    subsubcategory_name: 'Bänke',
    room_tags: [
      'livingroom',
      'kitchen_diningroom',
      'hallway'
    ],
    country_of_production: null,
    primary_material: 'Textil',
    secondary_material: 'Holz',
    main_color: 'Olivgrün',
    style: 'Skandinavisch',
    period: '2020er',
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in sehr gutem Zustand. Das Produkt weist kleine Schönheitsfehler auf, in der Regel sind das oberflächliche Verschmutzungen, unauffällige Kratzer oder Makel im nicht sichtbaren Bereich.',
    status_of_condition: 'Sehr guter Zustand',
    pdp_url: 'https://www.cocoli.com/de/produkte/umi-sitz-und-essbank-free-sage',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMkl1aGc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--19f6f6341e655202b2b51b82c930b09083e8acaf/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/COCOLI_SOFACOMPANY_Umi_bench_5.jpg',
    height: 40,
    width: 161,
    depth: 44,
    retail_price: 305.0,
    retail_price_i: 305,
    sale_price: 305.0,
    original_price: 436.0,
    discount_percentage: 30,
    has_discount: true,
    premium: false,
    sorting: 1762174959,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '252812',
    height_width: null,
    objectID: '172291',
    _highlightResult: {
      id_i: {
        value: '172291',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'SOFACOMPANY',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Umi Sitz- und Essbank Free Sage',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'chairs',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Stühle',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'benches',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Bänke',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'kitchen_diningroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'hallway',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Textil',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Holz',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Olivgrün',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Skandinavisch',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '2020er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Sehr guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '252812',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 171149,
    article_number: 'DOVI452',
    ean: null,
    brand_name: null,
    title: 'Vintage Carlo Moretti Vase Blau Weiß 1970er Jahre',
    category: 'decor',
    category_name: 'Deko',
    subcategory: 'vases',
    subcategory_name: 'Vasen',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom',
      'kitchen_diningroom'
    ],
    country_of_production: 'Italien',
    primary_material: 'Glas',
    secondary_material: null,
    main_color: 'Blau',
    style: 'Midcentury',
    period: '1970er',
    type_of_condition: 'Vintage',
    describe_condition: 'Es handelt sich um ein gebrauchtes Vintage-Produkt. Der Artikel befindet sich in einem guten Zustand und weist dem Alter entsprechende Gebrauchsspuren auf. Es handelt sich um kleine Kratzer oder Flecken, die den Fotos entnommen werden können.',
    status_of_condition: 'Guter Zustand',
    pdp_url: 'https://www.cocoli.com/de/produkte/vintage-carlo-moretti-vase-blau-weiss-1970er-jahre',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBeG9FZ0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--49b1396225124f11afbabb854c80badaf9d1c2f7/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/il_fullxfull.7111474290_8hn6.jpg',
    height: 24,
    width: 12,
    depth: 12,
    retail_price: 177.0,
    retail_price_i: 177,
    sale_price: 177.0,
    original_price: null,
    discount_percentage: 0,
    has_discount: false,
    premium: null,
    sorting: 1762174416,
    seller_kind: 'Vintage Händler',
    channel_ids: [
      1,
      2
    ],
    ext_sku: 'DOVI452',
    height_width: null,
    objectID: '171149',
    _highlightResult: {
      id_i: {
        value: '171149',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Vintage Carlo Moretti Vase Blau Weiß 1970er Jahre',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'decor',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Deko',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'vases',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Vasen',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'kitchen_diningroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Italien',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Glas',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Blau',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Midcentury',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '1970er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Vintage',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Vintage Händler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: 'DOVI452',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  }
]
const DEFAULT_AUSTRIAN_PRODUCTS = [
  {
    id_i: 172617,
    article_number: '2416',
    ean: '5710302024165',
    brand_name: 'Umage',
    title: 'Cassini Pendelleuchte Mini Weiß',
    category: 'lighting',
    category_name: 'Lampen',
    subcategory: 'pendant_lighting',
    subcategory_name: 'Pendelleuchten',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom',
      'office',
      'bathroom',
      'kitchen_diningroom',
      'hallway'
    ],
    country_of_production: null,
    primary_material: 'Stahl',
    secondary_material: 'Aluminium',
    main_color: 'Weiß',
    style: 'Minimalistisch',
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/at/produkte/cassini-pendelleuchte-mini-weiss',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBOE9RaGc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--0c1a325b89aea608f49538d282f0d42a925263db/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lKYW5CbFp3WTZCa1ZVT2hSeVpYTnBlbVZmZEc5ZmJHbHRhWFJiQjJrQzRBRnBBdUFCIiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--967306f8e3101f8fd891bd62ff1ff28c293c6243/UMAGE_lifestyle_Cassini_mini_white.jpeg',
    height: 11,
    width: 20,
    depth: 20,
    retail_price: 113.0,
    retail_price_i: 113,
    sale_price: 113.0,
    original_price: 226.0,
    discount_percentage: 50,
    has_discount: true,
    premium: false,
    sorting: 1762341387,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '2416',
    height_width: null,
    objectID: '172617',
    _highlightResult: {
      id_i: {
        value: '172617',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '5710302024165',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Umage',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Cassini Pendelleuchte Mini Weiß',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'lighting',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Lampen',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'pendant_lighting',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Pendelleuchten',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'office',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bathroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'kitchen_diningroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'hallway',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Stahl',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Aluminium',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Weiß',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Minimalistisch',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '2416',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 157096,
    article_number: '1000416861',
    ean: '5704924014512',
    brand_name: 'design for the people',
    title: '1-Flammig Wandleuchte Shapes Aluminium Messing',
    category: 'lighting',
    category_name: 'Lampen',
    subcategory: 'wall_lamps',
    subcategory_name: 'Wandleuchten',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom',
      'office',
      'hallway'
    ],
    country_of_production: null,
    primary_material: 'Glas',
    secondary_material: 'Aluminium',
    main_color: 'Gold',
    style: 'Zeitgenössisch',
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in sehr gutem Zustand. Das Produkt weist kleine Schönheitsfehler auf, in der Regel sind das oberflächliche Verschmutzungen, unauffällige Kratzer oder Makel im nicht sichtbaren Bereich.',
    status_of_condition: 'Sehr guter Zustand',
    pdp_url: 'https://www.cocoli.com/at/produkte/1-flammig-wandleuchte-shapes-aluminium-messing',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBelUwYnc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--91999bfaa79314bace252d369d90d36f803ea83c/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/-1000416861-240119-021-MOOD-DETAILS-P000000001000416861-mood.jpg',
    height: 26,
    width: 22,
    depth: 23,
    retail_price: 94.0,
    retail_price_i: 94,
    sale_price: 75.0,
    original_price: 125.0,
    discount_percentage: 40,
    has_discount: true,
    premium: false,
    sorting: 1762323986,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1
    ],
    ext_sku: '1000416861',
    height_width: null,
    objectID: '157096',
    _highlightResult: {
      id_i: {
        value: '157096',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '5704924014512',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'design for the people',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '1-Flammig Wandleuchte Shapes Aluminium Messing',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'lighting',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Lampen',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'wall_lamps',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Wandleuchten',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'office',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'hallway',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Glas',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Aluminium',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Gold',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Zeitgenössisch',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Sehr guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '1000416861',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 136253,
    article_number: '83118650',
    ean: '5714714135662',
    brand_name: 'TEMPUR',
    title: 'PRO® CoolQuilt Matratze 23cm Mittelfest 90cm Breite',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'beds',
    subcategory_name: 'Betten',
    subsubcategory: [
      'mattresses'
    ],
    subsubcategory_name: 'Matratzen',
    room_tags: [
      'bedroom'
    ],
    country_of_production: 'Dänemark',
    primary_material: 'Textil',
    secondary_material: null,
    main_color: 'Weiß',
    style: null,
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Diese Tempur® Matratzen stammen aus retournierten Produkten, die entweder direkt von Tempur® oder über autorisierte Händler verkauft wurden. Diese Rücksendungen erfolgen häufig nach einer Probeschlafphase, wenn sich der Kunde für ein anderes Modell entschieden hat. Die Matratzen werden sorgfältig geprüft und gereinigt. Die Bezüge werden bei maximal 60 °C desinfiziert und bei Bedarf durch Original Tempur® Bezüge ersetzt. Kleine Schönheitsfehler wie leichte Verfärbungen oder Abnutzungserscheinungen am Matratzenkern oder minimale Beschädigungen oder Abnutzungserscheinungen an der Außenhülle können auftreten, aber jede Matratze entspricht den hohen Tempur® Qualitätsstandards und ist hygienisch einwandfrei.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/at/produkte/pro-coolquilt-matratze-23cm-medium-firm-90cm-breite',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBeTV0WWc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--69e336641c797df7a280ca23279faf9840b8091a/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/1_Tempur+Pro+CoolQuilt+Single+in+Dark+Grey+Medium+Firm+Hybrid+1_FFFFFF.jpg',
    height: 200,
    width: 90,
    depth: 23,
    retail_price: 879.0,
    retail_price_i: 879,
    sale_price: 879.0,
    original_price: 2198.0,
    discount_percentage: 60,
    has_discount: true,
    premium: false,
    sorting: 1762323948,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2
    ],
    ext_sku: '83118650',
    height_width: 'ca. 200 x 90',
    objectID: '136253',
    _highlightResult: {
      id_i: {
        value: '136253',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '5714714135662',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'TEMPUR',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'PRO® CoolQuilt Matratze 23cm Mittelfest 90cm Breite',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'beds',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Betten',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'mattresses',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Matratzen',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Dänemark',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Textil',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Weiß',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '83118650',
        matchLevel: 'none',
        matchedWords: []
      },
      height_width: {
        value: 'ca. 200 x 90',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 151114,
    article_number: 'xJUAkJORp',
    ean: null,
    brand_name: 'MYCS',
    title: 'Pyllow Ecksofa Webstoff Anthrazit',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches'
    ],
    subsubcategory_name: 'Sofas & Couches',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Baumwolle',
    secondary_material: 'Leinen',
    main_color: 'Anthrazit',
    style: 'Modern',
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in sehr gutem Zustand. Das Produkt weist kleine Schönheitsfehler auf, in der Regel sind das oberflächliche Verschmutzungen, unauffällige Kratzer oder Makel im nicht sichtbaren Bereich.',
    status_of_condition: 'Sehr guter Zustand',
    pdp_url: 'https://www.cocoli.com/at/produkte/pyllow-ecksofa-webstoff-anthrazit',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBd3l1ZWc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--d1e8790a30b0074fb34953f4558e383bbf5c2268/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBPZ2h3Ym1jNkZISmxjMmw2WlY5MGIxOXNhVzFwZEZzSGFRTGdBV2tDNEFFPSIsImV4cCI6bnVsbCwicHVyIjoidmFyaWF0aW9uIn19--70f730e19e2d86b4a3aff00d1944d6555a3a1596/27d7529ba18254eae2615c7aa10e5491.webp',
    height: 72,
    width: 310,
    depth: 168,
    retail_price: 989.0,
    retail_price_i: 989,
    sale_price: 989.0,
    original_price: 2391.0,
    discount_percentage: 59,
    has_discount: true,
    premium: false,
    sorting: 1762257113,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: 'xJUAkJORp',
    height_width: null,
    objectID: '151114',
    _highlightResult: {
      id_i: {
        value: '151114',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'MYCS',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Pyllow Ecksofa Webstoff Anthrazit',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'sofas',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Sofas & Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'sofas_and_couches',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Sofas & Couches',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Baumwolle',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Leinen',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Anthrazit',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Modern',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Sehr guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: 'xJUAkJORp',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 155204,
    article_number: '12123154',
    ean: null,
    brand_name: 'Beatris',
    title: 'Versal 180 Boxspringbett Silber',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'beds',
    subcategory_name: 'Betten',
    subsubcategory: [
      'double_beds'
    ],
    subsubcategory_name: 'Doppelbetten',
    room_tags: [
      'bedroom'
    ],
    country_of_production: 'Polen',
    primary_material: 'Holz',
    secondary_material: 'Polyester',
    main_color: 'Silber',
    style: null,
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in gutem Zustand. Das Produkt weist geringe Mängel auf, In der Regel sind das leichte bis mittlere Verschmutzungen, Kratzer oder Makel, auch im sichtbaren Bereich. Das Produkt ist voll funktionsfähig. Die Bodenplane ist an manchen Stellen eingerissen, das Kopfende weist Bohrungen auf und der Stoff ist stellenweise verschmutzt. Bitte beachte die Zustandsbilder.',
    status_of_condition: 'Guter Zustand',
    pdp_url: 'https://www.cocoli.com/at/produkte/versal-180-boxspringbett-silber',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMnpCWlE9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--a524c74063a95a60aeba99b4b5a2632c7da24cae/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/PS_12123154_4.jpg',
    height: 133,
    width: 196,
    depth: 212,
    retail_price: 600.0,
    retail_price_i: 600,
    sale_price: 600.0,
    original_price: 2999.0,
    discount_percentage: 80,
    has_discount: true,
    premium: false,
    sorting: 1762249586,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '12123154',
    height_width: null,
    objectID: '155204',
    _highlightResult: {
      id_i: {
        value: '155204',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Beatris',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Versal 180 Boxspringbett Silber',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'beds',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Betten',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'double_beds',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Doppelbetten',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Polen',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Holz',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Silber',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '12123154',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 151536,
    article_number: '125605',
    ean: null,
    brand_name: 'Verpan',
    title: 'Easy Chair Dedar Karandash 006 Blau',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'armchairs'
    ],
    subsubcategory_name: 'Sessel',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Textil',
    secondary_material: null,
    main_color: 'Blau',
    style: 'Skandinavisch',
    period: null,
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in gutem Zustand. Das Produkt weist geringe Mängel auf, In der Regel sind das leichte bis mittlere Verschmutzungen, Kratzer oder Makel, auch im sichtbaren Bereich. Das Produkt ist voll funktionsfähig. Der Sessel ist an der Seite leicht beschädigt und der Fuß weist Kratzer auf. Bitte beachte die Zustandsbilder.',
    status_of_condition: 'Guter Zustand',
    pdp_url: 'https://www.cocoli.com/at/produkte/easy-chair-dedar-karandash-006-blau',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBNDhkV3c9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--3a2c3ade7b8b60e051cef12d314db948d74d0af1/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lKYW1acFpnWTZCa1ZVT2hSeVpYTnBlbVZmZEc5ZmJHbHRhWFJiQjJrQzRBRnBBdUFCIiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--643aaa5dbe434096b66bdf621b220d2c3aecbd19/proxy-image%20(3).jfif',
    height: 76,
    width: 80,
    depth: 80,
    retail_price: 1535.0,
    retail_price_i: 1535,
    sale_price: 1535.0,
    original_price: 3069.0,
    discount_percentage: 50,
    has_discount: true,
    premium: true,
    sorting: 1762248866,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '125605',
    height_width: null,
    objectID: '151536',
    _highlightResult: {
      id_i: {
        value: '151536',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Verpan',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Easy Chair Dedar Karandash 006 Blau',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'sofas',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Sofas & Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'armchairs',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Textil',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Blau',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Skandinavisch',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '125605',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 100543,
    article_number: 'TG-373',
    ean: null,
    brand_name: 'Design by Michel Ducaroy',
    title: 'Togo Sofa 2-Sitzer Samt Olivgrün',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'two_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,2-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Samt',
    secondary_material: null,
    main_color: 'Grün',
    style: 'Modern',
    period: '1970er',
    type_of_condition: 'Neu aufgearbeitet',
    describe_condition: 'Es handelt sich um ein mit neuen Stoffen aufgearbeitetes Vintage Produkt. Besondere Makel sind nicht bekannt, aber wie bei jedem Vintage Produkt können Gebrauchsspuren vorhanden sein, die durch den Lauf der Zeit entstanden sind. Die originalen Markenetiketten werden nach der Aufbereitung nicht wieder aufgenäht. Alle Togo Sofas werden mit einem gedruckten Authentizitätszertifikat vom Polsterer geliefert.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/at/produkte/togo-sofa-2-sitzer-samt-gruen',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBNFUwVGc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--dd2525f32f40a8cce2c415bb397428315d9e6dec/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBPZ2h3Ym1jNkZISmxjMmw2WlY5MGIxOXNhVzFwZEZzSGFRTGdBV2tDNEFFPSIsImV4cCI6bnVsbCwicHVyIjoidmFyaWF0aW9uIn19--70f730e19e2d86b4a3aff00d1944d6555a3a1596/373a.webp',
    height: 70,
    width: 130,
    depth: 100,
    retail_price: 2495.0,
    retail_price_i: 2495,
    sale_price: 2495.0,
    original_price: null,
    discount_percentage: 0,
    has_discount: false,
    premium: true,
    sorting: 1762182000,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2
    ],
    ext_sku: 'TG-373',
    height_width: null,
    objectID: '100543',
    _highlightResult: {
      id_i: {
        value: '100543',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Design by Michel Ducaroy',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Togo Sofa 2-Sitzer Samt Olivgrün',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'sofas',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Sofas & Sessel',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'sofas_and_couches',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'two_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Sofas & Couches,2-Sitzer',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Samt',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Grün',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Modern',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '1970er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Neu aufgearbeitet',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: 'TG-373',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 172291,
    article_number: '252812',
    ean: null,
    brand_name: 'SOFACOMPANY',
    title: 'Umi Sitz- und Essbank Free Sage',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'chairs',
    subcategory_name: 'Stühle',
    subsubcategory: [
      'benches'
    ],
    subsubcategory_name: 'Bänke',
    room_tags: [
      'livingroom',
      'kitchen_diningroom',
      'hallway'
    ],
    country_of_production: null,
    primary_material: 'Textil',
    secondary_material: 'Holz',
    main_color: 'Olivgrün',
    style: 'Skandinavisch',
    period: '2020er',
    type_of_condition: 'B-Ware',
    describe_condition: 'Es handelt sich um B-Ware, Outlet Ware oder Ausstellungsstücke in sehr gutem Zustand. Das Produkt weist kleine Schönheitsfehler auf, in der Regel sind das oberflächliche Verschmutzungen, unauffällige Kratzer oder Makel im nicht sichtbaren Bereich.',
    status_of_condition: 'Sehr guter Zustand',
    pdp_url: 'https://www.cocoli.com/at/produkte/umi-sitz-und-essbank-free-sage',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMkl1aGc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--19f6f6341e655202b2b51b82c930b09083e8acaf/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/COCOLI_SOFACOMPANY_Umi_bench_5.jpg',
    height: 40,
    width: 161,
    depth: 44,
    retail_price: 305.0,
    retail_price_i: 305,
    sale_price: 305.0,
    original_price: 436.0,
    discount_percentage: 30,
    has_discount: true,
    premium: false,
    sorting: 1762174959,
    seller_kind: 'Hersteller',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '252812',
    height_width: null,
    objectID: '172291',
    _highlightResult: {
      id_i: {
        value: '172291',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'SOFACOMPANY',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Umi Sitz- und Essbank Free Sage',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'chairs',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Stühle',
        matchLevel: 'none',
        matchedWords: []
      },
      subsubcategory: [
        {
          value: 'benches',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: 'Bänke',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'kitchen_diningroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'hallway',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Textil',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Holz',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Olivgrün',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Skandinavisch',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '2020er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'B-Ware',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Sehr guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Hersteller',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '252812',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 171149,
    article_number: 'DOVI452',
    ean: null,
    brand_name: null,
    title: 'Vintage Carlo Moretti Vase Blau Weiß 1970er Jahre',
    category: 'decor',
    category_name: 'Deko',
    subcategory: 'vases',
    subcategory_name: 'Vasen',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom',
      'kitchen_diningroom'
    ],
    country_of_production: 'Italien',
    primary_material: 'Glas',
    secondary_material: null,
    main_color: 'Blau',
    style: 'Midcentury',
    period: '1970er',
    type_of_condition: 'Vintage',
    describe_condition: 'Es handelt sich um ein gebrauchtes Vintage-Produkt. Der Artikel befindet sich in einem guten Zustand und weist dem Alter entsprechende Gebrauchsspuren auf. Es handelt sich um kleine Kratzer oder Flecken, die den Fotos entnommen werden können.',
    status_of_condition: 'Guter Zustand',
    pdp_url: 'https://www.cocoli.com/at/produkte/vintage-carlo-moretti-vase-blau-weiss-1970er-jahre',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBeG9FZ0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--49b1396225124f11afbabb854c80badaf9d1c2f7/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/il_fullxfull.7111474290_8hn6.jpg',
    height: 24,
    width: 12,
    depth: 12,
    retail_price: 177.0,
    retail_price_i: 177,
    sale_price: 177.0,
    original_price: null,
    discount_percentage: 0,
    has_discount: false,
    premium: null,
    sorting: 1762174416,
    seller_kind: 'Vintage Händler',
    channel_ids: [
      1,
      2
    ],
    ext_sku: 'DOVI452',
    height_width: null,
    objectID: '171149',
    _highlightResult: {
      id_i: {
        value: '171149',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: 'Vintage Carlo Moretti Vase Blau Weiß 1970er Jahre',
        matchLevel: 'none',
        matchedWords: []
      },
      category: {
        value: 'decor',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Deko',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'vases',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Vasen',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'kitchen_diningroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Italien',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Glas',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Blau',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Midcentury',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '1970er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Vintage',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Guter Zustand',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Vintage Händler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: 'DOVI452',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  }
]
const DEFAULT_ITALIAN_PRODUCTS = [
  {
    id_i: 135404,
    article_number: '560ARE1356',
    ean: '8683342766593',
    brand_name: 'Evila Originals',
    title: 'Sofa 3-Sitzer Orange',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'sofa_beds',
      'three_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,Schlafsofas,3-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Polyester',
    secondary_material: 'Holzfurnier',
    main_color: 'Orange',
    style: null,
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/sofa-3-sitzer-textil-orange',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMFFtT0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--4aadbc195337020765567c8f2716148464bb5b5c/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/560ARE1356%20-%201.jpg',
    height: 83,
    width: 239,
    depth: 99,
    retail_price: 900.0,
    retail_price_i: 900,
    sale_price: 810.0,
    original_price: 1964.0,
    discount_percentage: 59,
    has_discount: true,
    premium: false,
    sorting: 1766816748,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '560ARE1356',
    height_width: null,
    objectID: '135404',
    _highlightResult: {
      id_i: {
        value: '135404',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8683342766593',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> 3-Sitzer Orange',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: '<em>sofa</em>_beds',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'three_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,Schlafsofas,3-Sitzer',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Holzfurnier',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Orange',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '560ARE1356',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 135342,
    article_number: '560ARE1714',
    ean: '8682870704480',
    brand_name: 'Evila Originals',
    title: 'Sofa Récamiere Links Beige',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'corner_sofas',
      'three_seaters',
      'four_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,Ecksofas,3-Sitzer,4-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Polyester',
    secondary_material: null,
    main_color: 'Beige',
    style: 'Zeitgenössisch',
    period: '2020er',
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/sofa-recamiere-links-polyester-holz-beige',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBeThrT0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--2a87748c91cf6ba4f9a677c916c5bdc69764f320/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/560ARE1714%20-%201.jpg',
    height: 78,
    width: 100,
    depth: 198,
    retail_price: 1238.0,
    retail_price_i: 1238,
    sale_price: 1114.0,
    original_price: 2699.0,
    discount_percentage: 59,
    has_discount: true,
    premium: false,
    sorting: 1766814768,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '560ARE1714',
    height_width: null,
    objectID: '135342',
    _highlightResult: {
      id_i: {
        value: '135342',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8682870704480',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> Récamiere Links Beige',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'corner_<em>sofa</em>s',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'three_seaters',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'four_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,Ecksofas,3-Sitzer,4-Sitzer',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Beige',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Zeitgenössisch',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '2020er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '560ARE1714',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 140994,
    article_number: '372VDN1751',
    ean: '8683743791422',
    brand_name: 'Elizabed',
    title: 'Sofadecke Recycelte Baumwolle Beige',
    category: 'textiles',
    category_name: 'Textilien',
    subcategory: 'blankets',
    subcategory_name: 'Decken',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom'
    ],
    country_of_production: null,
    primary_material: 'Baumwolle',
    secondary_material: null,
    main_color: 'Beige',
    style: null,
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/merlin-sofadecke-baumwolle-beige',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBNGI4UlE9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--8f0e8b5627bcb3a0e3dc3b030fc50471c0fe755d/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/COCOLI_textiles_blankets_ELIZABED_372VDN1751_image_2_800px.jpg',
    height: 205,
    width: 170,
    depth: 1,
    retail_price: 10.0,
    retail_price_i: 10,
    sale_price: 9.0,
    original_price: 21.0,
    discount_percentage: 57,
    has_discount: true,
    premium: false,
    sorting: 1766813208,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '372VDN1751',
    height_width: null,
    objectID: '140994',
    _highlightResult: {
      id_i: {
        value: '140994',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8683743791422',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Elizabed',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em>decke Recycelte Baumwolle Beige',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'textiles',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Textilien',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'blankets',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Decken',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Baumwolle',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Beige',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '372VDN1751',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 140995,
    article_number: '372VDN1769',
    ean: '8684282043508',
    brand_name: 'Elizabed',
    title: 'Sofadecke Recycelte Baumwolle Ecru',
    category: 'textiles',
    category_name: 'Textilien',
    subcategory: 'blankets',
    subcategory_name: 'Decken',
    subsubcategory: [],
    subsubcategory_name: null,
    room_tags: [
      'livingroom',
      'bedroom'
    ],
    country_of_production: null,
    primary_material: 'Baumwolle',
    secondary_material: null,
    main_color: 'Creme',
    style: null,
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/merlin-sofadecke-baumwoll-ecru',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBM1lqUkE9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--17c7ba623f27e8e2fd6fe721072dfed160e06931/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/372VDN1769%20-%201.jpg',
    height: 205,
    width: 170,
    depth: 1,
    retail_price: 10.0,
    retail_price_i: 10,
    sale_price: 9.0,
    original_price: 21.0,
    discount_percentage: 57,
    has_discount: true,
    premium: false,
    sorting: 1766813148,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '372VDN1769',
    height_width: null,
    objectID: '140995',
    _highlightResult: {
      id_i: {
        value: '140995',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8684282043508',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Elizabed',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em>decke Recycelte Baumwolle Ecru',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'textiles',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Textilien',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: 'blankets',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory_name: {
        value: 'Decken',
        matchLevel: 'none',
        matchedWords: []
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        },
        {
          value: 'bedroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Baumwolle',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Creme',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '372VDN1769',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 150599,
    article_number: '560ARE2219',
    ean: '8684282469797',
    brand_name: 'Evila Originals',
    title: 'Sofa Récamiere Rechts Beige',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'sofa_beds',
      'corner_sofas'
    ],
    subsubcategory_name: 'Sofas & Couches,Schlafsofas,Ecksofas',
    room_tags: [
      'livingroom'
    ],
    country_of_production: 'Türkei',
    primary_material: 'Kunstfaser',
    secondary_material: 'Holzfurnier',
    main_color: 'Grau',
    style: null,
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/pirlo-rechts-ecksofa-beige',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMVJaVnc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--fd0bbd71beada5382c2065aea0524a4be7b01f7e/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/560ARE2219%20-%204.jpg',
    height: 78,
    width: 260,
    depth: 170,
    retail_price: 1570.0,
    retail_price_i: 1570,
    sale_price: 1413.0,
    original_price: 2826.0,
    discount_percentage: 50,
    has_discount: true,
    premium: false,
    sorting: 1741017686,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '560ARE2219',
    height_width: null,
    objectID: '150599',
    _highlightResult: {
      id_i: {
        value: '150599',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8684282469797',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> Récamiere Rechts Beige',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: '<em>sofa</em>_beds',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'corner_<em>sofa</em>s',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,Schlafsofas,Ecksofas',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      country_of_production: {
        value: 'Türkei',
        matchLevel: 'none',
        matchedWords: []
      },
      primary_material: {
        value: 'Kunstfaser',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Holzfurnier',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Grau',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '560ARE2219',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 135338,
    article_number: '560ARE1855',
    ean: '8683742690689',
    brand_name: 'Evila Originals',
    title: 'Sofa Récamiere Rechts Creme',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'corner_sofas',
      'three_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,Ecksofas,3-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Polyester',
    secondary_material: 'Technisches Holz',
    main_color: 'Creme',
    style: 'Zeitgenössisch',
    period: '2020er',
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/sofa-recamiere-rechts-polyester-holz-creme',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBNzhiT0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--8d0321384cfb18f340d2b9bfe00e7c7909bd4f0d/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/560ARE1855%20-%201.jpg',
    height: 73,
    width: 290,
    depth: 165,
    retail_price: 1430.0,
    retail_price_i: 1430,
    sale_price: 1287.0,
    original_price: 2615.0,
    discount_percentage: 51,
    has_discount: true,
    premium: false,
    sorting: 1723448186,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '560ARE1855',
    height_width: null,
    objectID: '135338',
    _highlightResult: {
      id_i: {
        value: '135338',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8683742690689',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> Récamiere Rechts Creme',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'corner_<em>sofa</em>s',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'three_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,Ecksofas,3-Sitzer',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Technisches Holz',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Creme',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Zeitgenössisch',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '2020er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '560ARE1855',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 135340,
    article_number: '560ARE1168',
    ean: '8682870193543',
    brand_name: 'Evila Originals',
    title: 'Sofa Récamiere Rechts Ecru',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'corner_sofas',
      'three_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,Ecksofas,3-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Polyester',
    secondary_material: 'Technisches Holz',
    main_color: 'Beige',
    style: 'Zeitgenössisch',
    period: '2020er',
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/sofa-recamiere-rechts-polyester-holz-ecru',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMlVoT0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--a2b19cd56bf10d689492acf1c785bfff0228197d/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/560ARE1168%20-%202.jpg',
    height: 92,
    width: 100,
    depth: 100,
    retail_price: 1438.0,
    retail_price_i: 1438,
    sale_price: 1294.0,
    original_price: 2618.0,
    discount_percentage: 51,
    has_discount: true,
    premium: false,
    sorting: 1723448066,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '560ARE1168',
    height_width: null,
    objectID: '135340',
    _highlightResult: {
      id_i: {
        value: '135340',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8682870193543',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> Récamiere Rechts Ecru',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'corner_<em>sofa</em>s',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'three_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,Ecksofas,3-Sitzer',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Technisches Holz',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Beige',
        matchLevel: 'none',
        matchedWords: []
      },
      style: {
        value: 'Zeitgenössisch',
        matchLevel: 'none',
        matchedWords: []
      },
      period: {
        value: '2020er',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '560ARE1168',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 135371,
    article_number: '825BLC2773',
    ean: '8683743274291',
    brand_name: 'Evila Originals',
    title: 'Sofa 2-Sitzer Anthrazit',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'two_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,2-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Polyester',
    secondary_material: 'Holzfurnier',
    main_color: 'Grau',
    style: null,
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/sofa-2-sitzer-textil-anthrazit',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBMzE0T0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--afd96c936c89dcd65b9fd6f920156ff1c7756188/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/825BLC2773%20-%201.jpg',
    height: 82,
    width: 155,
    depth: 90,
    retail_price: 393.0,
    retail_price_i: 393,
    sale_price: 354.0,
    original_price: 753.0,
    discount_percentage: 53,
    has_discount: true,
    premium: false,
    sorting: 1723446206,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '825BLC2773',
    height_width: null,
    objectID: '135371',
    _highlightResult: {
      id_i: {
        value: '135371',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8683743274291',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> 2-Sitzer Anthrazit',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'two_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,2-Sitzer',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Holzfurnier',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Grau',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '825BLC2773',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  },
  {
    id_i: 135372,
    article_number: '825BLC1498',
    ean: '8681875520194',
    brand_name: 'Evila Originals',
    title: 'Sofa 2-Sitzer Grau',
    category: 'furniture',
    category_name: 'Möbel',
    subcategory: 'sofas',
    subcategory_name: 'Sofas & Sessel',
    subsubcategory: [
      'sofas_and_couches',
      'two_seaters'
    ],
    subsubcategory_name: 'Sofas & Couches,2-Sitzer',
    room_tags: [
      'livingroom'
    ],
    country_of_production: null,
    primary_material: 'Polyester',
    secondary_material: 'Holzfurnier',
    main_color: 'Grau',
    style: null,
    period: null,
    type_of_condition: 'Auslaufmodell',
    describe_condition: 'Das Produkt ist neu und weist dementsprechend keinerlei Mängel oder Gebrauchsspuren auf.',
    status_of_condition: 'Wie neu',
    pdp_url: 'https://www.cocoli.com/de/produkte/sofa-2-sitzer-textil-grau',
    image_primary_url: 'https://d2v5b6dndmmv0y.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBOTkzT0E9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--c6743d0f4c46c7647a2015bdd8ce93431e22b38a/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaDdCem9MWm05eWJXRjBTU0lJYW5CbkJqb0dSVlE2RkhKbGMybDZaVjkwYjE5c2FXMXBkRnNIYVFMZ0FXa0M0QUU9IiwiZXhwIjpudWxsLCJwdXIiOiJ2YXJpYXRpb24ifX0=--3237b1f3cd4039da1d33c160bc3fd010f22b7bb4/825BLC1498%20-%201.jpg',
    height: 85,
    width: 177,
    depth: 81,
    retail_price: 336.0,
    retail_price_i: 336,
    sale_price: 302.0,
    original_price: 665.0,
    discount_percentage: 55,
    has_discount: true,
    premium: false,
    sorting: 1723446146,
    seller_kind: 'Offizieller Markenhändler',
    channel_ids: [
      1,
      2,
      3
    ],
    ext_sku: '825BLC1498',
    height_width: null,
    objectID: '135372',
    _highlightResult: {
      id_i: {
        value: '135372',
        matchLevel: 'none',
        matchedWords: []
      },
      ean: {
        value: '8681875520194',
        matchLevel: 'none',
        matchedWords: []
      },
      brand_name: {
        value: 'Evila Originals',
        matchLevel: 'none',
        matchedWords: []
      },
      title: {
        value: '<em>Sofa</em> 2-Sitzer Grau',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      category: {
        value: 'furniture',
        matchLevel: 'none',
        matchedWords: []
      },
      category_name: {
        value: 'Möbel',
        matchLevel: 'none',
        matchedWords: []
      },
      subcategory: {
        value: '<em>sofa</em>s',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subcategory_name: {
        value: '<em>Sofa</em>s & Sessel',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      subsubcategory: [
        {
          value: '<em>sofa</em>s_and_couches',
          matchLevel: 'full',
          fullyHighlighted: false,
          matchedWords: [
            'sofa'
          ]
        },
        {
          value: 'two_seaters',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      subsubcategory_name: {
        value: '<em>Sofa</em>s & Couches,2-Sitzer',
        matchLevel: 'full',
        fullyHighlighted: false,
        matchedWords: [
          'sofa'
        ]
      },
      room_tags: [
        {
          value: 'livingroom',
          matchLevel: 'none',
          matchedWords: []
        }
      ],
      primary_material: {
        value: 'Polyester',
        matchLevel: 'none',
        matchedWords: []
      },
      secondary_material: {
        value: 'Holzfurnier',
        matchLevel: 'none',
        matchedWords: []
      },
      main_color: {
        value: 'Grau',
        matchLevel: 'none',
        matchedWords: []
      },
      type_of_condition: {
        value: 'Auslaufmodell',
        matchLevel: 'none',
        matchedWords: []
      },
      status_of_condition: {
        value: 'Wie neu',
        matchLevel: 'none',
        matchedWords: []
      },
      seller_kind: {
        value: 'Offizieller Markenhändler',
        matchLevel: 'none',
        matchedWords: []
      },
      ext_sku: {
        value: '825BLC1498',
        matchLevel: 'none',
        matchedWords: []
      }
    }
  }
]

export default class extends Controller {
  static values = {
    placeholder: String,
    cancelButton: String,
    locale: String,
    translations: Object,
    defaultState: Object
  }

  connect () {
    this.abortController = null
    this.autocompleteInstance = this.initializeAutocomplete()
  }

  disconnect () {
    if (this.debouncedFederatedSearch) this.debouncedFederatedSearch.cancel()
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.autocompleteInstance.destroy()
  }

  defaultProducts () {
    if (COUNTRY === 'at') {
      return DEFAULT_AUSTRIAN_PRODUCTS
    } else if (COUNTRY === 'it') {
      return DEFAULT_ITALIAN_PRODUCTS
    } else {
      return DEFAULT_GERMAN_PRODUCTS
    }
  }

  fallbackDefaultState () {
    return {
      suggestions: [],
      categories: DEFAULT_CATEGORIES,
      brands: [],
      products: this.defaultProducts()
    }
  }

  t (key) {
    return this.translationsValue?.autocomplete?.[key] || key
  }

  searchUrl (query) {
    return `${SEARCH_PATH}?query=${encodeURIComponent(query)}`
  }

  autocompleteUrl (query, channelId) {
    return `${AUTOCOMPLETE_PATH}?query=${encodeURIComponent(query || '')}&channel_id=${channelId}`
  }

  localizedProductUrl (url, countryCode) {
    const parsedUrl = new URL(url, window.location.origin)
    const segments = parsedUrl.pathname.split('/').filter(Boolean)

    if (LANGUAGE_CODES.includes(segments[0]) && COUNTRY_CODES.includes(segments[1])) {
      segments.splice(0, 2)
    } else if (COUNTRY_CODES.includes(segments[0])) {
      segments.shift()
    }

    if (segments.length === 0) {
      segments.push(ITEMS_PATH_SEGMENT)
    } else {
      segments[0] = ITEMS_PATH_SEGMENT
    }

    parsedUrl.pathname = `/${LANGUAGE}/${countryCode}/${segments.join('/')}`

    return parsedUrl.toString()
  }

  translateCategory (categoryKey) {
    const translations = this.translationsValue?.category_translations || {}
    return translations[categoryKey]
  }

  handleFavoriteClick (event) {
    event.preventDefault()
    event.stopPropagation()

    const checkbox = event.target
      .closest('label')
      .querySelector('input[type="checkbox"]')

    if (checkbox) {
      checkbox.click()
    }
  }

  initializeAutocomplete () {
    const countryCode = this.localeValue
    const controller = this

    // Share one federated result across sources and avoid duplicate requests
    let lastQuery
    let lastResultsPromise
    const getFederatedResults = (query) => {
      if (!query || query.length < 2) {
        const dbDefaults = this.defaultStateValue || {}
        const fallback = this.fallbackDefaultState()
        return Promise.resolve({
          suggestions: dbDefaults.suggestions || fallback.suggestions,
          categories: dbDefaults.categories?.length ? dbDefaults.categories : fallback.categories,
          brands: dbDefaults.brands?.length ? dbDefaults.brands : fallback.brands,
          products: dbDefaults.products?.length ? dbDefaults.products : fallback.products
        })
      }

      if (query === lastQuery && lastResultsPromise) return lastResultsPromise
      lastQuery = query
      lastResultsPromise = debouncedFederatedSearch(query).catch(() => ({ categories: [], brands: [], products: [] }))
      return lastResultsPromise
    }

    const debouncedFederatedSearch = debouncePromise((query) => {
      if (this.abortController) this.abortController.abort()
      this.abortController = new AbortController()

      const channelId = COUNTRY === 'de' ? 1 : COUNTRY === 'at' ? 2 : COUNTRY === 'it' ? 3 : 1
      const autocompleteUrl = this.autocompleteUrl(query, channelId)

      return fetch(autocompleteUrl, { signal: this.abortController.signal })
        .then(response => {
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`)
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          return {
            suggestions: data.suggestions || [],
            categories: data.categories || [],
            brands: data.brands || [],
            products: data.products || []
          }
        })
    }, DEBOUNCE_MS)

    this.debouncedFederatedSearch = debouncedFederatedSearch

    // Query Suggestions from federated results (single request)
    const querySuggestionsPlugin = {
      async getSources ({ query }) {
        let { suggestions } = await getFederatedResults(query)
        suggestions = suggestions?.slice(0, 5) || []
        if (!suggestions.length) return []
        const items = (suggestions || []).map((hit) => ({
          text: hit.text,
          url: hit.url,
          style: hit.style,
          query: hit.query || query,
          __autocomplete_qsCategory: hit.__autocomplete_qsCategory
        }))
        return [
          {
            sourceId: 'querySuggestionsPlugin',
            getItems () {
              return items
            },
            getItemUrl ({ item }) {
              if (item.url) return item.url
              const finalQuery = item.__autocomplete_qsCategory
                ? `${item.query}+${item.__autocomplete_qsCategory}`
                : item.query
              return controller.searchUrl(finalQuery)
            },
            templates: {
              item ({ item }) {
                if (item.url && item.text) {
                  return html`<a
                    class="aa-ItemLinkCustom ${item.style || ''}"
                    href="${item.url}"
                  >
                    <div class="aa-ItemWrapper">
                      <div class="aa-ItemContent">
                        <div class="aa-ItemContentTitle">${item.text}</div>
                      </div>
                    </div>
                  </a>`
                }
                const finalQuery = item.__autocomplete_qsCategory
                  ? `${item.query}+${item.__autocomplete_qsCategory}`
                  : item.query
                return html`<a
                  class="aa-ItemLinkCustom"
                  href="${controller.searchUrl(finalQuery)}"
                >
                  <div class="aa-ItemWrapper">
                    <div class="aa-ItemContent">
                      <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                          <path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z" />
                        </svg>
                      </div>
                      <div class="aa-ItemContentTitle">${item.query}</div>
                      ${item.__autocomplete_qsCategory && html`<div class="aa-ItemContentSubtitle aa-ItemContentSubtitle--standalone">- in<span> ${item.__autocomplete_qsCategory} </span></div>`}
                    </div>
                  </div>
                </a>`
              }
            }
          }
        ]
      }
    }

    const brandSuggestionsPlugin = {
      async getSources ({ query }) {
        const { brands } = await getFederatedResults(query)
        return [
          {
            sourceId: 'brandSuggestions',
            getItems () {
              const label = (item) => item.brandName || ''
              return (brands || []).sort((a, b) => label(a).localeCompare(label(b)))
            },
            templates: {
              header () {
                return html`
                  <span class="aa-SourceHeader pt-4"
                    >${controller.t('brands')}</span
                  >
                `
              },
              item ({ item }) {
                const label = item.brandName || ''
                return html`
                  <a
                    class="aa-ItemLinkCustom ${item.style || ''}"
                    href="/${countryCode}/search?query=${encodeURIComponent(label)}"
                  >
                    <div class="aa-ItemWrapper">
                      <div class="aa-ItemContent">
                        <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                          <svg
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="currentColor"
                          >
                            <path
                              d="M12 2L2 7h20L12 2zm0 2.18L18.09 7H5.91L12 4.18zM2 9v11h20V9H2zm2 2h16v7H4v-7z"
                            />
                          </svg>
                        </div>
                        <div class="aa-ItemContentTitle">${label}</div>
                      </div>
                    </div>
                  </a>
                `
              }
            }
          }
        ]
      }
    }

    // Category Suggestions Plugin
    const categorySuggestionsPlugin = {
      async getSources ({ query }) {
        const { categories } = await getFederatedResults(query)
        return [
          {
            sourceId: 'categorySuggestions',
            getItems () {
              const catList = (categories || []).filter((c) => c.customLink || controller.translateCategory(c.categoryName))
              const label = (item) => item.text ?? controller.translateCategory(item.categoryName) ?? ''
              return catList.sort((a, b) => label(a).localeCompare(label(b)))
            },
            templates: {
              header () {
                return html`
                  <span class="aa-SourceHeader pt-4"
                    >${controller.t('categories')}</span
                  >
                `
              },
              item ({ item }) {
                const href = item.url || `/${countryCode}/search?query=${encodeURIComponent(item.categoryName)}`
                const label = item.text || controller.translateCategory(item.categoryName)
                return html`
                  <a
                    class="aa-ItemLinkCustom ${item.style || ''}"
                    href="${href}"
                  >
                    <div class="aa-ItemWrapper">
                      <div class="aa-ItemContent">
                        <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                          <svg
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="currentColor"
                          >
                            <path
                              d="M12 2L2 7h20L12 2zm0 2.18L18.09 7H5.91L12 4.18zM2 9v11h20V9H2zm2 2h16v7H4v-7z"
                            />
                          </svg>
                        </div>
                        <div class="aa-ItemContentTitle">${label}</div>
                      </div>
                    </div>
                  </a>
                `
              }
            }
          }
        ]
      }
    }

    const productsPlugin = {
      async getSources ({ query }) {
        const { products } = await getFederatedResults(query)
        return [
          {
            sourceId: 'productsPlugin',
            getItems () {
              return products
            },
            templates: {
              header () {
                return html`
                  <span class="aa-SourceHeader"
                    >${controller.t('products')}</span
                  >
                `
              },
              item ({ item, components }) {
                const title =
                  item._highlightResult && item._highlightResult.title
                    ? item._highlightResult.title.value
                    : item.title

                const currentPrice = item.sale_price || item.retail_price
                const originalPrice = item.original_price
                const discountPercentage = item.discount_percentage
                const hasDiscount = item.has_discount && discountPercentage > 0

                const formatPriceDisplay = (current, original, discount) => {
                  if (!current) return ''
                  const formatPrice = (price) => {
                    const formatted = `${price.toFixed(2)} €`
                    return formatted.replace('.00 €', ' €')
                  }

                  const currentPriceStr = formatPrice(current)

                  if (hasDiscount && original) {
                    return html`
                      <div class="d-flex flex-wrap align-items-baseline gap-1">
                        <span class="fw-bold fs-6 text-dark"
                          >${currentPriceStr}</span
                        >
                        <span
                          class="text-muted fw-light text-uppercase"
                          style="font-size: 0.75rem;"
                        >
                          ${controller.t('neu')}
                        </span>
                        <span
                          class="text-muted fw-light text-decoration-line-through"
                          style="font-size: 0.75rem;"
                        >
                          ${formatPrice(original)}
                        </span>

                        <span
                          class="text-muted fw-light"
                          style="font-size: 0.75rem;"
                        >
                          | -${discount}%
                        </span>
                      </div>
                    `
                  }

                  return html`<span class="fw-bold fs-6 text-dark"
                    >${currentPriceStr}</span
                  >`
                }
                const localeCorrectedUrl = controller.localizedProductUrl(item.pdp_url, countryCode)
                return html`
                  <div
                    class="item-row product-card d-flex flex-column position-relative"
                    style="border:none; box-shadow:none;"
                  >
                    <a
                      href="${localeCorrectedUrl}"
                      class="text-decoration-none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        class="item-card-media aspect-ratio-keeper-image image-contain product-image"
                        src="${item.image_primary_url}"
                        width="120"
                        height="120"
                        alt="${item.title}"
                      />
                    </a>

                    <div class="mt-2 mb-1">
                      <div
                        class="text-muted mt-1 mb-1"
                        style="font-size: 0.75rem"
                      >
                        ${item.brand_name}
                      </div>
                      <a
                        href="${localeCorrectedUrl}"
                        class="text-decoration-none"
                        target="_blank"
                        rel="noopener noreferrer"
                        ref=${(el) => {
                    if (el) el.innerHTML = title
                  }}
                      ></a>
                    </div>

                    ${item.depth &&
                  item.width &&
                  item.height &&
                  html`
                      <div class="card-body-dimensions mb-1">
                        ${controller
                      .t('dimensions')
                      .replace('%{length}', item.depth)
                      .replace('%{width}', item.width)
                      .replace('%{height}', item.height)}
                      </div>
                    `}

                    <div
                      class="card-body-pricing mb-1 d-flex align-items-center justify-content-between"
                    >
                      <div>
                        ${formatPriceDisplay(
                        currentPrice,
                        originalPrice,
                        discountPercentage
                      )}
                      </div>

                      <label
                        class="favorite-btn ms-2"
                        data-controller="favorite"
                        data-favorite-target="label"
                        data-title="Add to wishlist"
                        onClick=${(event) =>
                    controller.handleFavoriteClick(event)}
                      >
                        <input
                          type="checkbox"
                          data-action="click->favorite#toggle"
                          data-favorite-target="checkbox"
                          data-url="/items/${item.objectID}/favorite"
                          id="add_to_favorites_${item.objectID}"
                          ${item.inFavorite ? 'checked' : ''}
                        />
                        <svg
                          width="25"
                          height="22"
                          version="1.1"
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          x="0px"
                          y="0px"
                          viewBox="0 0 129 119.9"
                          xml:space="preserve"
                        >
                          <g>
                            <path
                              d="M64.5,15.7c3-3.3,6.3-6.2,10.1-8.3c12.4-6.4,24.8-7,37,0.2c8.4,5,13,12.9,15.3,22.2c2.7,11.1,1.1,21.8-3.1,32.2
                              s-10.7,19.3-18.5,27.3c-10.9,11.2-23.4,20.3-37,27.8c-2.6,1.4-5,1.4-7.5,0c-14.7-8.2-28.2-18-39.5-30.5c-7.7-8.5-14-17.9-17.5-28.9
                              C0.2,46.9-0.5,36.1,3.5,25.3C7.7,14,15.3,6.1,27.2,3.5c14-3.1,26.1,0.8,36.2,11C63.7,14.9,64,15.2,64.5,15.7z M64.6,25.4
                              c-0.2-0.1-0.2-0.1-0.3-0.1c-0.2-0.2-0.3-0.4-0.4-0.7C58,15.4,49.8,9.5,38.7,8.5c-11.7-1.1-21.1,3-27.2,13.3
                              c-5.3,8.9-5.9,18.5-3.8,28.5c2.3,11.1,7.8,20.6,15,29.2c11.4,13.6,25.4,24,40.9,32.6c0.8,0.4,1.3,0.2,2-0.1
                              c12.2-6.7,23.3-14.6,33.3-24.4c8.2-8,15.2-17,19.5-27.7c3.8-9.4,5.3-19.1,2.8-29c-2.1-8.1-6.1-15-13.7-19
                              c-10.9-5.7-21.6-4.6-31.9,1.9C71,16.6,67.4,20.7,64.6,25.4z"
                              fill="#2F2E2E"
                            />
                            <path
                              id="filled"
                              d="M64.5,118.5c-1.3,0-2.6-0.4-3.9-1.1c-16-8.9-29-18.9-39.6-30.6c-8.6-9.5-14.3-18.9-17.5-29c-3.8-12-3.9-22.7-0.2-32.6
                              c4.5-12,12.5-19.4,23.9-21.9C41,0.2,53.3,4,63.5,14.4c0.2,0.2,0.5,0.5,0.7,0.8l0.2,0.3c3.4-3.7,6.6-6.3,10-8.1
                              c13-6.9,25.5-6.8,37.2,0.1c7.7,4.6,12.9,12.1,15.4,22.3c2.5,10.3,1.5,20.9-3.1,32.4c-3.8,9.5-9.9,18.5-18.5,27.4
                              c-10.3,10.6-22.4,19.8-37.1,27.9C67.1,118.1,65.8,118.5,64.5,118.5z M36.3,2.8c-2.9,0-5.9,0.3-9,1C16.1,6.2,8.2,13.5,3.7,25.3
                              C0,35.1,0.1,45.7,3.9,57.6c3.2,10,8.9,19.4,17.4,28.8C31.9,98,44.8,108,60.8,116.9c2.5,1.4,4.8,1.4,7.3,0
                              c14.6-8.1,26.7-17.2,37-27.8c8.6-8.9,14.7-17.8,18.4-27.3c4.6-11.4,5.6-21.9,3.1-32.1c-2.5-10.1-7.6-17.5-15.2-22
                              C99.9,1,87.6,0.9,74.8,7.7c-3.4,1.8-6.6,4.4-10.1,8.2l-0.2,0.2l-0.6-0.6c-0.3-0.3-0.5-0.5-0.7-0.8C55.3,6.8,46.3,2.8,36.3,2.8z
                               M64.6,116.6c-0.3,0-0.6-0.1-1.1-0.3c-18.4-10.1-32.8-21.6-44.1-35c-8.6-10.3-14-20.6-16.2-31.5C0.8,37.8,2.2,27.7,7.4,19
                              C13.9,8.3,23.8,3.4,36.9,4.6C48.1,5.7,57.3,11.5,64.2,22c0.1,0.2,0.3,0.4,0.4,0.6c3.3-5.5,7.1-9.6,11.7-12.5
                              c11.6-7.3,23.2-8,34.5-2c7.5,4,12.4,10.6,14.8,20.5s1.5,20.1-3,31.4c-4.2,10.4-10.9,19.9-21.1,29.9c-10.1,10-22.1,18.8-35.8,26.2
                              C65.4,116.4,65,116.6,64.6,116.6z"
                              fill="#2F2E2E"
                            />
                          </g>
                        </svg>
                      </label>
                    </div>
                  </div>
                `
              },

              footer ({ state }) {
                const trimmedQuery = state.query?.trim()
                const seeAllHref = trimmedQuery
                  ? controller.searchUrl(trimmedQuery)
                  : SHOP_PATH
                return html`
                  <div style="text-align:center;">
                    <a
                      href="${seeAllHref}"
                      class="aa-SeeAllBtn"
                    >
                      ${controller.t('see_all')}
                    </a>
                  </div>
                `
              }
            }
          }
        ]
      }
    }

    return autocomplete({
      container: this.element,
      placeholder: this.placeholderValue,
      plugins: [
        querySuggestionsPlugin,
        brandSuggestionsPlugin,
        categorySuggestionsPlugin,
        productsPlugin
      ],
      debug: import.meta.env.DEV,
      detachedMediaQuery: '(max-width: 768px)',
      translations: {
        detachedCancelButtonText: this.cancelButtonValue
      },
      openOnFocus: true,
      initialState: {
        query: new URL(window.location).searchParams.get('query') || ''
      },
      reshape ({ sourcesBySourceId, sources, state }) {
        const {
          querySuggestionsPlugin: querySuggestions,
          brandSuggestions,
          categorySuggestions,
          productsPlugin: products
        } = sourcesBySourceId

        return [
          [querySuggestions, brandSuggestions, categorySuggestions].filter(
            Boolean
          ),
          [products].filter(Boolean)
        ]
      },

      render: ({ elements, state }, root) => {
        const {
          querySuggestionsPlugin: querySuggestions,
          brandSuggestions,
          categorySuggestions,
          productsPlugin: products
        } = elements

        render(
          html`
            <div class="position-relative">
              <button
                class="aa-CloseButton btn btn-link text-decoration-none position-absolute end-0 me-1 p-0 text-dark fs-2"
                type="button"
                aria-label="Close"
              >
                ×
              </button>

              <div class="aa-PanelLayout aa-Panel--scrollable">
                <div class="aa-PanelSections d-flex">
                  <div class="aa-PanelSection--left col-3">
                    <div class="aa-SourceHeader">
                      <span class="aa-SourceHeader"
                        >${controller.t('suggestions')}</span
                      >
                    </div>
                    <div class="aa-PanelSectionSources">
                      ${querySuggestions}
                      <div class="pt-4">${brandSuggestions}</div>
                      <div class="pt-4">${categorySuggestions}</div>
                    </div>
                  </div>
                  <div class="vertical-divider mx-2 d-none d-md-block"></div>
                  <div class="aa-PanelSection--right d-none d-md-block">
                    ${products &&
            html`
                      <div class="aa-PanelSection--products position-relative">
                        <div class="aa-PanelSectionSource">${products}</div>
                      </div>
                    `}
                  </div>
                </div>
              </div>
            </div>
          `,
          root
        )

        this.initializeStimulusControllers(root)
      },

      onSubmit: (item, event) => {
        if (this.debouncedFederatedSearch) this.debouncedFederatedSearch.cancel()
        if (this.abortController) {
          this.abortController.abort()
          this.abortController = null
        }

        const queryString = stringify({ query: item.state.query })
        const path = `${window.location.origin}${SEARCH_PATH}?${queryString}`
        Turbo.visit(path)
      }
    })
  }

  initializeStimulusControllers (root) {
    const closeBtn = root.querySelector('.aa-CloseButton')
    if (closeBtn && !closeBtn.dataset.clickAttached) {
      closeBtn.dataset.clickAttached = 'true'
      closeBtn.addEventListener('click', () => {
        this.autocompleteInstance.setIsOpen(false)
      })
    }
    const favoriteElements = root.querySelectorAll(
      "[data-controller~='favorite']"
    )
    favoriteElements.forEach((el) => {
      if (!el.__stimulusControllerConnected) {
        this.application?.getControllerForElementAndIdentifier?.(el, 'favorite')
        el.__stimulusControllerConnected = true
      }
    })
  }
}
